/**
 * AddToCartButton Component
 */

import { requestAddItemToCart } from '../../../../redux/actions/cart';
import { pushNotification } from '../../../../redux/actions/notifications';
import { pushPostAuthenticationRequestPage } from '../../../../redux/actions/postAuthenticationRequests';
import { showSignUpForm } from '../../../../redux/actions/ui';
import { getResponsiveTrackThumbnail } from '../../../../utils/track';
import './styles.scss';

import { WithRouter, withRouter } from 'found';
import { merge } from 'lodash';
import * as React from 'react';
import { connect, DispatchProp } from 'react-redux';

/* tslint:disable-next-line */
import 'velocity-animate/velocity.ui';

import { CurrencyContext } from '../../../../contexts/currency';
import { ICurrencyContext } from '../../../../contexts/currency/types';
import { IQueryStringContext, QueryStringContext } from '../../../../contexts/queryString';
import { licenseUsagesFromQueryStringSelector } from '../../../../contexts/queryString/selectors';
import { CheckoutPagePath } from '../../../pages/Checkout';
import { IconAddToCart } from '../../images/Icon';
import Button, { ButtonProps } from '../Button';

interface IOwnStateProps {
  sessionLicense: ILicenseUsage[];
  userIsLoggedIn: boolean;
  viewerId?: string;
}

interface IOwnDispatchProps extends Required<DispatchProp<any>> {
  addToCart(licenseUsages: ILicenseUsage[], viewerId?: string): Promise<any>;
}

interface IOwnMergedProps {
  addToCart(): Promise<any>;
  pushAddedToCartNotification(): void;
}

interface IOwnProps extends ButtonProps {
  item: GQL.ITrackSearchResult;
  usages?: ILicenseUsage[];
  stopPropagationOnClick?: boolean;
  onItemAddedToCart?(): void;
}

type IConnectedOwnProps =
  & IOwnProps
  & Pick<ICurrencyContext, 'currency'>
  & Pick<IQueryStringContext, 'license_params'>
  & WithRouter;

export type AddToCartButtonOwnProps = IOwnProps;
type AddToCartButtonProps = IOwnProps & Required<DispatchProp<any>> & IOwnMergedProps & WithRouter;

interface IOwnState {
  submitting: boolean;
}

const defaultList = [];

class AddToCartButton extends React.Component<AddToCartButtonProps, IOwnState> {
  defaultButtonProps: Partial<ButtonProps> = {
    colorScheme: 'accent',
    icon: <IconAddToCart />,
    label: 'Add to Cart',
    size: 'xsmall',
  };

  isComponentMounted: boolean;

  constructor(props) {
    super(props);

    this.state = {
      submitting: false,
    };
  }

  componentDidMount() {
    this.isComponentMounted = true;
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
  }

  onClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (this.props.stopPropagationOnClick) {
      e.stopPropagation();
    }

    if (this.props.onClick) {
      e.persist();
      this.props.onClick(e);
    }

    this.setState({ submitting: true }, async () => {
      const {
        addToCart,
        onItemAddedToCart,
        pushAddedToCartNotification,
        dispatch,
      } = this.props;

      try {
        await addToCart();

        if (onItemAddedToCart) {
          onItemAddedToCart();
        }

        pushAddedToCartNotification();
      } catch (error) {
        const message = error[0].message;
        dispatch(pushNotification({
          autoDismiss: false,
          /* tslint:disable-next-line */
          message: `We were unable to add this track to your cart. Please try again later or contact us. (${message})`,
          state: 'error',
        }));
        console.error('failed to add to cart', message);
      }

      if (this.isComponentMounted) {
        this.setState({ submitting: false });
      }
    });
  }

  render() {
    const {
      addToCart,
      dispatch,
      item,
      onClick,
      onItemAddedToCart,
      pushAddedToCartNotification,
      stopPropagationOnClick,
      router,
      ...buttonProps
    } = this.props;

    const props = merge(
      this.defaultButtonProps,
      buttonProps,
    );

    return (
      <Button
        title="Add this track to cart"
        disabled={this.state.submitting}
        submitting={this.state.submitting}
        onClick={this.onClick}
        className="add-to-cart-button"
        {...props}
      />
    );
  }
}

// tslint:disable-next-line:max-line-length
const ConnectedAddToCartButton = connect<IOwnStateProps, IOwnDispatchProps, IConnectedOwnProps, AddToCartButtonProps, IStoreState>(
  (state, ownProps) => ({
    sessionLicense: licenseUsagesFromQueryStringSelector(state, ownProps),
    viewerId: state.user.viewerId,
    userIsLoggedIn: state.user.isLoggedIn,
  }),
  (dispatch, ownProps) => ({
    dispatch,
    addToCart: (terms, viewerId) =>
      new Promise((resolve, reject) => {
        dispatch(requestAddItemToCart({
          lineItem: {
            terms,
            track: ownProps.item,
          },
          currencyCode: ownProps.currency,
          viewerId: viewerId || '',
          promise: { resolve, reject },
        }));
      }),
  }),
  ({ sessionLicense, userIsLoggedIn, viewerId }, { addToCart, dispatch }, { currency, usages, ...ownProps}) => ({
    ...ownProps,
    dispatch,
    addToCart: () => addToCart(usages || sessionLicense || defaultList, viewerId),
    pushAddedToCartNotification: () => {
      const trackTitle = ownProps.item.title!;
      const trackThumbnail = getResponsiveTrackThumbnail(ownProps.item);
      dispatch(pushNotification({
        autoDismiss: 5000,
        image: trackThumbnail.medium![0].path,
        message: `Track <em>${trackTitle}</em> was added to your cart.`,
        action: 'Checkout Now',
        onActionClick: () => {
          if (!userIsLoggedIn) {
            dispatch(pushPostAuthenticationRequestPage({ page: CheckoutPagePath }));
            dispatch(showSignUpForm({ reason: 'checkout' }));
          } else {
            ownProps.router.push(CheckoutPagePath);
          }
        },
      }));
    },
  }),
)(AddToCartButton);

const AddToCartButtonWithContext: React.SFC<IOwnProps & WithRouter> = (props) => (
  <CurrencyContext.Consumer>
    {({ currency }) => (
      <QueryStringContext.Consumer>
        {({ license_params }) => (
          <ConnectedAddToCartButton currency={currency} license_params={license_params} {...props} />
        )}
      </QueryStringContext.Consumer>
    )}
  </CurrencyContext.Consumer>
);

export default withRouter(AddToCartButtonWithContext);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/atoms/buttons/AddToCartButton/index.tsx